import { useCallback, useMemo, useRef, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { PRODUCT_MIX_REPORT, PRODUCT_MIX_REPORT_COUNT } from "../product-mix-report/fragment";
import { createApolloClient } from "../../../../providers/ApolloClientFactory";
import { useOrganizationStore } from "../../../../store/organization";
import { useEventStore } from "../../../../store/event";
import { userStore } from "../../../../store/user";
import { ReportFilters } from "../../components/ReportFilters";
import { SecondaryButton } from "../../../../components/Button";
import CustomSelect from "../../../../components/customSelect";
import { DisplayType, ProductMixReportRow } from "./types";
import {
  aggregateByDay,
  aggregateByDayByLocation,
  aggregateByItem,
  aggregateByLocation,
  pivotByHour,
  pivotByUser,
} from "./helpers";
import { SummaryBar } from "./SummaryBar";
import { ItemTable } from "./ItemTable";
import { GroupCard } from "./GroupCard";
import { PivotTable } from "./PivotTable";
import { exportProductMixCsv } from "./exportCsv";

const PAGE_SIZE = 50000;
const client = createApolloClient();

const displayTypeOptions: { value: DisplayType; label: string }[] = [
  { value: "by_item", label: "By Item" },
  { value: "by_day_by_item", label: "By Day → By Item" },
  { value: "by_location_by_item", label: "By Location → By Item" },
  { value: "by_day_by_location_by_item", label: "By Day → By Location → By Item" },
  { value: "by_hour_by_item_pivot", label: "By Hour → By Item (Pivot)" },
  { value: "by_user_by_item_pivot", label: "By User → By Item (Pivot)" },
];

export const ProductMixUpdated = () => {
  const location = useLocation();
  const currentOrgId = useOrganizationStore((s) => s.organizationId);
  const currentEventId = useEventStore((s) => s.eventId);

  const [data, setData] = useState<ProductMixReportRow[]>([]);
  const [displayType, setDisplayType] = useState<DisplayType>("by_item");
  const [isLoading, setIsLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ loaded: number; total: number } | null>(null);
  const [initialUsernames, setInitialUsernames] = useState<{ label: string; value: string }[]>([]);
  const [initialLocations, setInitialLocations] = useState<{ label: string; value: string }[]>([]);
  const mountedRef = useRef(true);

  useEffect(() => () => { mountedRef.current = false; }, []);

  const buildWhere = useCallback(() => {
    const organizationId = parseInt(currentOrgId !== null ? currentOrgId.toString() : "", 10);
    const eventId = currentEventId;
    const user = userStore.getState().user;

    const search = new URLSearchParams(location.search);
    const where = search.get("where");
    let whereVariable: any = where ? JSON.parse(where) : {};

    if (organizationId && organizationId !== 0) {
      if (user?.vendors && !whereVariable.vendor_id) {
        whereVariable.vendor_id = { _in: user.vendors };
      }
      whereVariable.organization_id = { _eq: organizationId };
    }

    if (eventId) {
      whereVariable.event_id = { _eq: eventId };
    } else if (user?.events) {
      whereVariable.event_id = { _in: user.events };
    }
    return whereVariable;
  }, [location.search, currentOrgId, currentEventId]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setProgress(null);
    try {
      const whereVariable = buildWhere();

      const { data: totalData } = await client.query({
        query: PRODUCT_MIX_REPORT_COUNT,
        variables: { where: whereVariable, limit: 0 },
        fetchPolicy: "network-only",
      });
      const totalCount: number = totalData?.total?.aggregate?.count ?? 0;
      setProgress({ loaded: 0, total: totalCount });

      const allRows: ProductMixReportRow[] = [];
      let offset = 0;
      while (offset < totalCount) {
        const { data: chunk } = await client.query({
          query: PRODUCT_MIX_REPORT,
          variables: { where: whereVariable, limit: PAGE_SIZE, offset },
          fetchPolicy: "network-only",
        });
        const rows = (chunk?.reports_product_mix as ProductMixReportRow[]) ?? [];
        allRows.push(...rows);
        offset += PAGE_SIZE;
        if (mountedRef.current) setProgress({ loaded: Math.min(offset, totalCount), total: totalCount });
      }

      if (!mountedRef.current) return;
      setData(allRows);

      const locs = new Map<string, string>();
      const usernames = new Set<string>();
      allRows.forEach((r) => {
        if (r.location_id && r.location_name) locs.set(String(r.location_id), String(r.location_name));
        if (r.username) usernames.add(r.username);
      });
      setInitialLocations(
        [...locs.entries()]
          .sort((a, b) => a[1].localeCompare(b[1], undefined, { sensitivity: "base" }))
          .map(([value, label]) => ({ label, value }))
      );
      setInitialUsernames(
        [...usernames]
          .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
          .map((u) => ({ label: u, value: u }))
      );
      setHasGenerated(true);
    } catch (err: any) {
      console.error("Error fetching product mix:", err);
      if (mountedRef.current) setError(err.message || "Failed to load data");
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
        setProgress(null);
      }
    }
  }, [buildWhere]);

  const itemAgg = useMemo(() => (displayType === "by_item" ? aggregateByItem(data) : null), [data, displayType]);
  const dayAgg = useMemo(() => (displayType === "by_day_by_item" ? aggregateByDay(data) : null), [data, displayType]);
  const locAgg = useMemo(() => (displayType === "by_location_by_item" ? aggregateByLocation(data) : null), [data, displayType]);
  const dayLocAgg = useMemo(
    () => (displayType === "by_day_by_location_by_item" ? aggregateByDayByLocation(data) : null),
    [data, displayType]
  );
  const hourPivot = useMemo(() => (displayType === "by_hour_by_item_pivot" ? pivotByHour(data) : null), [data, displayType]);
  const userPivot = useMemo(() => (displayType === "by_user_by_item_pivot" ? pivotByUser(data) : null), [data, displayType]);

  const activeTotals =
    itemAgg?.totals ||
    dayAgg?.totals ||
    locAgg?.totals ||
    dayLocAgg?.totals ||
    null;

  const distinctItemCount = useMemo(() => new Set(data.map((r) => r.pos_name)).size, [data]);

  const canGenerate = !!currentOrgId;

  const handleExport = () => {
    exportProductMixCsv({
      displayType,
      searchString: location.search,
      itemView: itemAgg ? { items: itemAgg.items, totals: itemAgg.totals } : undefined,
      groupView: dayAgg
        ? { groups: dayAgg.groups, totals: dayAgg.totals, depth: 1, groupLabel: "Date" }
        : locAgg
          ? { groups: locAgg.groups, totals: locAgg.totals, depth: 1, groupLabel: "Location" }
          : dayLocAgg
            ? { groups: dayLocAgg.groups, totals: dayLocAgg.totals, depth: 2, groupLabel: "Date / Location" }
            : undefined,
      pivot: hourPivot || userPivot || undefined,
    });
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-4 pb-3 mb-4">
        <div className="flex items-center justify-between pt-3 pb-2">
          <h1 className="text-lg font-semibold text-gray-900">Product Mix Report (Updated)</h1>
          <span className="text-xs uppercase tracking-wide text-red-700 bg-red-50 border border-red-200 rounded px-2 py-0.5">Admin Preview</span>
        </div>
        <ReportFilters
          includeSubEvents
          includeDates
          includeTime
          includeLocations
          includeVendors
          eventScopedVendors
          includeUsernames
          usernameOptions={initialUsernames}
          locationOptions={initialLocations}
          customQueryPaths={{
            dateFrom: "_and[0].transaction_hour",
            dateTo: "_and[1].transaction_hour",
          }}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1 mb-3">
          <div>
            <CustomSelect
              options={displayTypeOptions as any}
              defaultValue={displayType}
              onChange={(value) => setDisplayType(value as DisplayType)}
              title="Grouping"
            />
          </div>
        </div>
        <div className="flex items-center justify-between pt-1">
          <div className="text-xs text-gray-500">
            {!canGenerate && "Select an organization to generate the report."}
            {canGenerate && hasGenerated && !isLoading && `Loaded ${data.length.toLocaleString()} rows.`}
            {isLoading && progress && progress.total > 0 && `Loading ${progress.loaded.toLocaleString()} / ${progress.total.toLocaleString()}…`}
            {isLoading && !progress && "Loading…"}
          </div>
          <div className="flex items-center gap-2">
            {hasGenerated && !isLoading && data.length > 0 && (
              <SecondaryButton onClick={handleExport}>Export to CSV</SecondaryButton>
            )}
            <button
              type="button"
              onClick={fetchData}
              disabled={!canGenerate || isLoading}
              className={`px-4 py-2 text-sm font-semibold rounded-lg shadow-sm transition-colors ${
                canGenerate && !isLoading
                  ? "bg-red-700 text-white hover:bg-red-800"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              {isLoading ? "Generating..." : "Generate Report"}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">{error}</div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-red-700" />
        </div>
      )}

      {!isLoading && !hasGenerated && !error && (
        <div className="text-center py-20 text-gray-400">
          Select your filters and click <span className="font-semibold text-gray-600">Generate Report</span>.
        </div>
      )}

      {!isLoading && hasGenerated && data.length === 0 && !error && (
        <div className="text-center py-20 text-gray-500">No data matched the current filters.</div>
      )}

      {!isLoading && hasGenerated && data.length > 0 && (
        <>
          {activeTotals && <SummaryBar totals={activeTotals} itemCount={distinctItemCount} />}

          {itemAgg && <ItemTable items={itemAgg.items} />}

          {dayAgg && (
            <div className="space-y-3">
              {dayAgg.groups.map((g) => (
                <GroupCard key={g.key} group={g} />
              ))}
            </div>
          )}

          {locAgg && (
            <div className="space-y-3">
              {locAgg.groups.map((g) => (
                <GroupCard key={g.key} group={g} />
              ))}
            </div>
          )}

          {dayLocAgg && (
            <div className="space-y-3">
              {dayLocAgg.groups.map((g) => (
                <GroupCard key={g.key} group={g} />
              ))}
            </div>
          )}

          {hourPivot && <PivotTable pivot={hourPivot} />}
          {userPivot && <PivotTable pivot={userPivot} />}
        </>
      )}
    </div>
  );
};
